import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert, Box, Button, CircularProgress, Divider,
  FormControlLabel, IconButton, LinearProgress, MenuItem, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { updateSupportDeskSettings } from "../../../../store/common/actions";

function SupportDeskSettings({ supportDeskSettings, updateSupportDeskSettings }) {
  const [enabled,       setEnabled]       = useState(false);
  const [otpRequired,   setOtpRequired]   = useState(false);
  const [slaHours,      setSlaHours]      = useState(24);
  const [warnSlaHours,  setWarnSlaHours]  = useState(4);
  const [allowTransferResolution, setAllowTransferResolution] = useState(false);
  const [allowPermutationResolution, setAllowPermutationResolution] = useState(false);
  const [letterEffectiveDate, setLetterEffectiveDate] = useState("");
  const [letterEffectiveDateUpdatedAt, setLetterEffectiveDateUpdatedAt] = useState(null);
  const [letterEffectiveDateValid, setLetterEffectiveDateValid] = useState(false);
  const [allowPostLimitResave, setAllowPostLimitResave] = useState(false);
  const [allowPostLimitResaveUpdatedAt, setAllowPostLimitResaveUpdatedAt] = useState(null);
  const [allowPostLimitResaveValid, setAllowPostLimitResaveValid] = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [error,         setError]         = useState("");

  const hydrate = (s) => {
    if (!s) return;
    setEnabled(!!s.enabled);
    setOtpRequired(!!s.otpRequired);
    setSlaHours(s.slaHours ?? 24);
    setWarnSlaHours(s.warnSlaHours ?? 4);
    setAllowTransferResolution(!!s.allowTransferResolution);
    setAllowPermutationResolution(!!s.allowPermutationResolution);
    // Only the admin GET/PUT (not the public settings this page also
    // receives via Redux) carry these — guard so that response doesn't
    // blank out an already-loaded date.
    if (s.letterEffectiveDate !== undefined) setLetterEffectiveDate(s.letterEffectiveDate || "");
    if (s.letterEffectiveDateUpdatedAt !== undefined) setLetterEffectiveDateUpdatedAt(s.letterEffectiveDateUpdatedAt);
    if (s.letterEffectiveDateValid !== undefined) setLetterEffectiveDateValid(!!s.letterEffectiveDateValid);
    // Only the admin GET/PUT carry these — guard so the public-settings
    // response pushed via Redux doesn't reset an already-loaded value.
    if (s.allowPostLimitResave !== undefined) setAllowPostLimitResave(!!s.allowPostLimitResave);
    if (s.allowPostLimitResaveUpdatedAt !== undefined) setAllowPostLimitResaveUpdatedAt(s.allowPostLimitResaveUpdatedAt);
    if (s.allowPostLimitResaveValid !== undefined) setAllowPostLimitResaveValid(!!s.allowPostLimitResaveValid);
  };

  useEffect(() => { hydrate(supportDeskSettings); }, [supportDeskSettings]);

  // The Redux-provided settings above come from the public endpoint, which
  // doesn't carry the letter-effective-date fields — load those directly.
  useEffect(() => {
    axios.get("/api/support-desk/settings").then(({ data }) => hydrate(data)).catch(() => {});
  }, []);

  const save = () => {
    if (slaHours < 1 || warnSlaHours < 1 || warnSlaHours >= slaHours) {
      setError("Warn SLA hours must be less than SLA hours, and both must be at least 1.");
      return;
    }
    setError("");
    setSaving(true);
    updateSupportDeskSettings(
      {
        enabled, otpRequired, slaHours: Number(slaHours), warnSlaHours: Number(warnSlaHours),
        allowTransferResolution, allowPermutationResolution, letterEffectiveDate,
        allowPostLimitResave,
      },
      (data) => { hydrate(data); setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    );
  };

  const daysAgo = letterEffectiveDateUpdatedAt
    ? Math.floor((Date.now() - new Date(letterEffectiveDateUpdatedAt).getTime()) / 86400000)
    : null;

  // The override is honoured for 2 hours after it was last saved on — keep this
  // in sync with POST_LIMIT_RESAVE_MAX_AGE_MS in api/.../post-limit-override.js.
  const postLimitResaveMinutesLeft =
    allowPostLimitResaveValid && allowPostLimitResaveUpdatedAt
      ? Math.max(0, Math.ceil(
          (new Date(allowPostLimitResaveUpdatedAt).getTime() + 2 * 60 * 60 * 1000 - Date.now()) / 60000
        ))
      : 0;

  return (
    <Box sx={{ maxWidth: 540 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: .5 }}>Support Desk</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control teacher access to the TMIS Support Desk. When disabled, the button is hidden on the
        home page and new ticket submissions are blocked. Support staff can still access the inbox
        to manage existing tickets.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Settings saved.</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            color="warning"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Enable Support Desk</Typography>
            <Typography variant="caption" color="text.secondary">
              Shows the "Support Desk" button on the TMIS home page and allows teachers to submit tickets.
            </Typography>
          </Box>
        }
        sx={{ mb: 2, alignItems: "flex-start" }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={otpRequired}
            onChange={(e) => setOtpRequired(e.target.checked)}
            color="warning"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Require OTP verification</Typography>
            <Typography variant="caption" color="text.secondary">
              After entering their National ID, teachers must enter a one-time code sent by SMS to the
              phone registered in IPPS before they can view or submit any request. Protects a teacher's
              tickets from anyone who merely knows their National ID.
            </Typography>
          </Box>
        }
        sx={{ mb: 3, alignItems: "flex-start" }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={allowTransferResolution}
            onChange={(e) => setAllowTransferResolution(e.target.checked)}
            color="warning"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Allow direct transfer resolution</Typography>
            <Typography variant="caption" color="text.secondary">
              Lets an agent resolve a Transfer ticket by picking a vacant destination post and
              executing a real transfer for that one requester — without opening the Transfer
              campaign window to every teacher. The teacher gets their official letter the usual way
              (Transfer requests page), not attached to the ticket. Requires the letter effective date
              below to be set. Off by default.
            </Typography>
          </Box>
        }
        sx={{ mb: 2, alignItems: "flex-start" }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={allowPermutationResolution}
            onChange={(e) => setAllowPermutationResolution(e.target.checked)}
            color="warning"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Allow direct permutation resolution</Typography>
            <Typography variant="caption" color="text.secondary">
              Lets an agent resolve a Permutation ticket by looking up the requester and a swap
              partner (by employee ID) and executing a real two-way swap between them — without
              opening the Permutation campaign window to every teacher. If the partner has their own
              open ticket, it's closed automatically too, with its own resolution recorded. A separate
              setting from transfer resolution above, since it's a different action. Requires the
              letter effective date below to be set. Off by default.
            </Typography>
          </Box>
        }
        sx={{ mb: 3, alignItems: "flex-start" }}
      />

      <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: letterEffectiveDateValid ? "#F6F8FC" : "#FEF3E0" }}>
        <Typography sx={{ fontWeight: 600, mb: .5 }}>Letter effective date</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Printed on transfer/permutation letters as the date the move takes effect. Direct
          transfer/permutation resolution (the two toggles above) is blocked unless this is set and
          has been confirmed within the last 7 days — re-enter it (even unchanged) and save to
          confirm it's still correct.
        </Typography>
        <TextField
          type="date" size="small" label="Effective date"
          value={letterEffectiveDate}
          onChange={(e) => setLetterEffectiveDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 220 }}
        />
        <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600, color: letterEffectiveDateValid ? "#12885A" : "#B7791F" }}>
          {!letterEffectiveDate
            ? "Not set — direct resolution is blocked until this is saved."
            : letterEffectiveDateValid
            ? `Confirmed ${daysAgo === 0 ? "today" : `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`} — valid for direct resolution.`
            : "Expired (last confirmed more than 7 days ago) — re-save to confirm before agents can use direct resolution."}
        </Typography>
      </Box>

      <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: allowPostLimitResaveValid ? "#FEF3E0" : "#F6F8FC" }}>
        <FormControlLabel
          control={
            <Switch
              checked={allowPostLimitResave}
              onChange={(e) => setAllowPostLimitResave(e.target.checked)}
              color="warning"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Allow re-saving post limits (2-hour window)</Typography>
              <Typography variant="caption" color="text.secondary">
                Post limits (A0/A1/A2) are normally locked once they've been set. Turn this on to let
                staff correct an already-set limit. It automatically switches off 2 hours after you save
                it, so the lock returns on its own. Save this page to (re)start the window.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start" }}
        />
        <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600, color: allowPostLimitResaveValid ? "#B7791F" : "#4A5A76" }}>
          {allowPostLimitResaveValid
            ? `Active — re-saving is unlocked for about ${postLimitResaveMinutesLeft} more minute${postLimitResaveMinutesLeft === 1 ? "" : "s"}.`
            : allowPostLimitResave
            ? "Expired (more than 2 hours since it was last saved on) — save to reopen the window."
            : "Off — post limits are locked once set."}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography sx={{ fontWeight: 700, mb: 2 }}>SLA Configuration</Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
        <TextField
          label="SLA hours"
          type="number"
          size="small"
          value={slaHours}
          onChange={(e) => setSlaHours(e.target.value)}
          helperText="Target resolution time (hours)"
          inputProps={{ min: 1 }}
          sx={{ maxWidth: 180 }}
        />
        <TextField
          label="Warn SLA hours"
          type="number"
          size="small"
          value={warnSlaHours}
          onChange={(e) => setWarnSlaHours(e.target.value)}
          helperText="Warn when this many hours remain"
          inputProps={{ min: 1 }}
          sx={{ maxWidth: 180 }}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="warning"
          onClick={save}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      <SupportCategories />
    </Box>
  );
}

// ── Configurable list of support request categories ───────────────────────────
function SupportCategories() {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [busyId,  setBusyId]  = useState(null);
  const [newName,  setNewName]  = useState("");
  const [newDesc,  setNewDesc]  = useState("");
  const [newColor, setNewColor] = useState("#5B6B85");

  const load = () => {
    setLoading(true);
    axios.get("/api/support-desk/categories")
      .then(({ data }) => setCats(data || []))
      .catch(() => setErr("Failed to load categories."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const patchLocal = (id, field, value) =>
    setCats((cs) => cs.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  const add = () => {
    if (!newName.trim()) return;
    setErr(""); setBusyId("new");
    axios.post("/api/support-desk/categories", { name: newName.trim(), description: newDesc.trim(), color: newColor })
      .then(({ data }) => { setCats((cs) => [...cs, data]); setNewName(""); setNewDesc(""); setNewColor("#5B6B85"); })
      .catch((e) => setErr(e?.response?.data || "Failed to add category."))
      .finally(() => setBusyId(null));
  };

  const saveRow = (c) => {
    setErr(""); setBusyId(c.id);
    axios.put(`/api/support-desk/categories/${c.id}`, {
      name: c.name, description: c.desc, color: c.color, sortOrder: c.sortOrder, isActive: c.isActive,
      subjectOptions: c.subjectOptions || [],
      resolutionType: c.resolutionType || "note",
    })
      .then(({ data }) => setCats((cs) => cs.map((x) => (x.id === c.id ? data : x))))
      .catch((e) => setErr(e?.response?.data || "Failed to save category."))
      .finally(() => setBusyId(null));
  };

  const remove = (c) => {
    setErr(""); setBusyId(c.id);
    axios.delete(`/api/support-desk/categories/${c.id}`)
      .then(() => setCats((cs) => cs.filter((x) => x.id !== c.id)))
      .catch((e) => setErr(e?.response?.data || "Failed to delete category."))
      .finally(() => setBusyId(null));
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography sx={{ fontWeight: 700, mb: .5 }}>Support request categories</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These are the options a teacher chooses from when raising a request. Inactive categories are
        hidden from teachers but kept so historical tickets still display correctly. A category used by
        existing tickets can't be deleted — deactivate it instead.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {cats.map((c) => (
        <Box key={c.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap", py: 1.5, borderBottom: "1px solid #eee", opacity: c.isActive ? 1 : .6 }}>
          <input
            type="color"
            value={c.color || "#5B6B85"}
            onChange={(e) => patchLocal(c.id, "color", e.target.value)}
            style={{ width: 34, height: 34, border: "none", background: "none", cursor: "pointer", flexShrink: 0 }}
            title="Category colour"
          />
          <Box sx={{ flex: "1 1 420px", minWidth: 220 }}>
            <TextField
              size="small" fullWidth label="Name" value={c.name || ""}
              onChange={(e) => patchLocal(c.id, "name", e.target.value)} sx={{ mb: 1 }}
            />
            <TextField
              size="small" fullWidth label="Description" value={c.desc || ""}
              onChange={(e) => patchLocal(c.id, "desc", e.target.value)}
              sx={{ mb: 1 }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4A5A76", mt: 1 }}>Subject field</Typography>
            <TextField
              size="small" fullWidth multiline minRows={3}
              label="Subject options"
              placeholder={"Internal transfer\nExternal transfer\nPermutation\nSpecial transfer"}
              InputLabelProps={{ shrink: true }}
              helperText="Enter one option per line to turn the teacher's Subject field into a dropdown of these choices. Leave empty to keep a free-text subject. Remember to click SAVE."
              value={(c.subjectOptions || []).join("\n")}
              onChange={(e) => patchLocal(c.id, "subjectOptions", e.target.value.split("\n"))}
              sx={{ mb: 1 }}
            />
            <TextField
              size="small" select label="Resolution"
              value={c.resolutionType || "note"}
              onChange={(e) => patchLocal(c.id, "resolutionType", e.target.value)}
              sx={{ minWidth: 240, mb: 1 }}
            >
              <MenuItem value="note">Default (resolution note)</MenuItem>
              <MenuItem value="origin_destination">Origin → destination (from/to district + school)</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>key: {c.key}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: .5 }}>
            <FormControlLabel
              control={<Switch size="small" checked={!!c.isActive} onChange={(e) => patchLocal(c.id, "isActive", e.target.checked)} />}
              label={<Typography variant="caption">Active</Typography>}
              sx={{ m: 0 }}
            />
            <Box>
              <Button size="small" onClick={() => saveRow(c)} disabled={busyId === c.id}>Save</Button>
              <Tooltip title="Delete">
                <span>
                  <IconButton size="small" color="error" onClick={() => remove(c)} disabled={busyId === c.id}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      ))}

      {/* Add new */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mt: 2, p: 2, background: "#f7f9fc", borderRadius: 2 }}>
        <input
          type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
          style={{ width: 34, height: 34, border: "none", background: "none", cursor: "pointer", flexShrink: 0 }}
          title="Category colour"
        />
        <TextField size="small" label="New category name" value={newName} onChange={(e) => setNewName(e.target.value)} sx={{ flex: "1 1 220px" }} />
        <TextField size="small" label="Description" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} sx={{ flex: "2 1 260px" }} />
        <Button variant="outlined" color="warning" onClick={add} disabled={busyId === "new" || !newName.trim()}>Add</Button>
      </Box>
    </Box>
  );
}

const mapStateToProps = ({ supportDeskSettings }) => ({ supportDeskSettings });
export default connect(mapStateToProps, { updateSupportDeskSettings })(SupportDeskSettings);
